import React, { useContext, useState, FC, useEffect } from 'react';

import Paper from '@material-ui/core/Paper';
import AnimatedNumber from 'react-animated-number';
import { Hidden } from '@material-ui/core';
import { isMobile } from 'react-device-detect';

import { GlobalContext } from '../../../../../../store';
import { IAdminMembership } from '../../../../../../store/admin/memberships/store';
import { IAdminInvitation } from '../../../../../../store/admin/invitations/store';

import Button from '../../../../../../components/Button';
import Input from '../../../../../../components/Input';
import Tooltip from '../../../../../../components/Tooltip';
import PlayerList from '../../../PlayerList/PlayerList';
import { getMemberSelection } from '../../Utils/getMemberSelection';
import { EventPlayersFieldStyled } from './EventPlayersField.Styles';

interface IProps {
  clubId: string;
  eventId: string;
  selectedMembers: IAdminMembership[];
  setSelectedMembers: (value: React.SetStateAction<IAdminMembership[]>) => void;
  onInvitationsSubmit: () => Promise<any>;
}

const EventPlayersField: FC<IProps> = props => {
  const { selectedMembers, setSelectedMembers, onInvitationsSubmit, eventId } = props;

  const {
    adminMemberships: { memberships },
    adminEventEnrollments: eventEnrollments,
    adminInvitations: invitations
  } = useContext(GlobalContext);

  const [playerSearch, setPlayerSearch] = useState('');
  const [invited, setInvited] = useState<number[]>([]);

  const [eventInvitations, setEventInvitations] = useState<IAdminInvitation[]>([]);

  const [allMembers, setAllMembers] = useState<IAdminMembership[]>([]);

  useEffect(() => {
    const enrollments = eventEnrollments[Number(eventId)];

    if (enrollments && enrollments.length > 0) {
      setInvited(enrollments.map(e => e.user.id));
    }
  }, [eventEnrollments, eventId]);

  useEffect(() => {
    const invitationsToSet = invitations.eventMemberships[Number(eventId)];

    if (invitationsToSet && invitationsToSet.length > 0) {
      setEventInvitations(invitationsToSet);
    }
  }, [invitations]);

  useEffect(() => {
    if (memberships && memberships.length > 0) {
      const fakeMembers = eventInvitations
        .filter(invitation => !invitation.accepted)
        .map(invitation => ({
          club: invitation.club.id,
          groups: [],
          id: invitation.id,
          status: '',
          user: {
            email: invitation.email,
            firstName: '',
            id: 0,
            lastName: '',
            profile: {
              birthDate: '',
              gender: '',
              ghinNumber: '',
              hasGhin: false
            }
          }
        }));

      setAllMembers(memberships.concat(fakeMembers));
    }
  }, [memberships.length, eventInvitations.length]);

  const onSearch = (evnt: any) => {
    setPlayerSearch(evnt.target.value);
  };

  const memberSelection = (member: IAdminMembership) =>
    setSelectedMembers(getMemberSelection(member, selectedMembers));

  return (
    <EventPlayersFieldStyled>
      <Hidden smDown={true}>
        <div className="title__container override">
          <div className="bot-16">PLAYERS FIELD</div>

          <Tooltip sectionKey="INVITE_PLAYERS.PLAYERS_FIELD" />
        </div>
      </Hidden>

      <Paper elevation={1} className="paper player-list-paper">
        <div className="titles-row">
          <Input
            label="FIND A PLAYER"
            id="playerfield-lookup"
            type="search"
            onChange={onSearch}
            outlined={true}
            placeholder="FIND A PLAYER"
          />

          <div className="total-label">TOTAL</div>

          <div className="total-value">
            <AnimatedNumber
              component="div"
              stepPrecision={0}
              value={selectedMembers.length}
              style={{
                transition: '0.8s ease-out',
                transitionProperty: 'background-color, color, opacity'
              }}
              duration={300}
            />
          </div>
        </div>

        <PlayerList
          eventId={Number(eventId)}
          members={allMembers}
          invited={invited}
          selectedMembers={selectedMembers}
          memberSelection={memberSelection}
          mobileVersion={isMobile}
          search={playerSearch}
        />
      </Paper>

      <p className="warning-paragraph override">
        Currently, you <strong>cannot</strong> save your list and send invitations later. If you do
        not send now the selected players will not be saved. If you send now, you can always return
        later to send more invites.
      </p>

      <div className="action-buttons full-width extra-size">
        <Button
          color="primary"
          variant="outlined"
          fullWidth={true}
          onClick={onInvitationsSubmit}
          type="submit"
        >
          CANCEL
        </Button>

        <Button
          id="send-invitations-button"
          color="primary"
          variant="contained"
          fullWidth={true}
          onClick={onInvitationsSubmit}
          type="submit"
        >
          SEND INVITATIONS
        </Button>
      </div>
    </EventPlayersFieldStyled>
  );
};

const areEqual = (prevProps: IProps, nextProps: IProps) => {
  return prevProps.selectedMembers.length === nextProps.selectedMembers.length;
};

export default React.memo(EventPlayersField, areEqual);
